import axios from "axios";
import React, { useEffect, useState } from "react";
import { Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL } from "../../utils/consts";
import { useSelector } from "react-redux";
import { useAuth } from "../../utils/useAuth";
import { isSuperAdmin } from "../../utils/permissions";
import { usePermissions } from "../../utils/usePermissions";
import AppModal from "../../Components/Modals/AppModal/AppModal";
import "./Suppliers.scss";

const PAGE_SIZE = 10;

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #f97316, #ec4899)",
  "linear-gradient(135deg, #3b82f6, #8b5cf6)",
  "linear-gradient(135deg, #10b981, #3b82f6)",
  "linear-gradient(135deg, #f59e0b, #ef4444)",
  "linear-gradient(135deg, #8b5cf6, #ec4899)",
  "linear-gradient(135deg, #06b6d4, #3b82f6)",
];

const getAvatarGradient = (seed: string) => {
  if (!seed) return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[seed.charCodeAt(seed.length - 1) % AVATAR_GRADIENTS.length];
};

const getInitials = (naziv = "") =>
  naziv.split(" ").slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase();

const PERSON_GRADIENTS = [
  "linear-gradient(135deg,#f97316,#ec4899)",
  "linear-gradient(135deg,#3b82f6,#8b5cf6)",
  "linear-gradient(135deg,#10b981,#3b82f6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#3b82f6)",
];
const personGradient = (id: string) =>
  PERSON_GRADIENTS[(id?.charCodeAt(id.length - 1) ?? 0) % PERSON_GRADIENTS.length];
const personInitials = (u: any) =>
  `${u?.firstName?.[0] ?? ""}${u?.lastName?.[0] ?? ""}`.toUpperCase();

const normalizeZaduzeni = (sup: any): any[] => {
  if (!sup?.zaduzeni) return [];
  return Array.isArray(sup.zaduzeni) ? sup.zaduzeni : [sup.zaduzeni];
};

const emptyForm = {
  naziv: "",
  kontaktOsoba: "",
  email: "",
  telefon: "",
  komercijalist: "",
  zaduzeniIds: [] as string[],
  datumNarudzbe: "",
  napomena: "",
};

const TruckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </svg>
);

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<any>(undefined);
  const [users, setUsers] = useState<any[]>([]);
  const userData = useSelector((state: any) => state.userData.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<any>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [nazivError, setNazivError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [showUserDrop, setShowUserDrop] = useState(false);
  const navigate = useNavigate();
  const authUser: any = useAuth();
  const { hasPermission } = usePermissions();
  const isSuper = isSuperAdmin(userData?.roles || authUser?.roles);
  const canView        = hasPermission("suppliers:view")         || isSuper;
  const canCreate      = hasPermission("suppliers:create")       || isSuper;
  const canDelete      = hasPermission("suppliers:delete")       || isSuper;
  const canOpenProfile = hasPermission("suppliers:open-profile") || isSuper;

  const clientId =
    typeof userData?.clientId === "object" ? userData?.clientId?._id : userData?.clientId;

  useEffect(() => {
    if (clientId) {
      axios
        .get(`${BASE_API_URL}/${clientId}/suppliers`)
        .then((res) => setSuppliers(res.data))
        .catch(() => setSuppliers([]));
      axios
        .get(`${BASE_API_URL}/${clientId}/users`)
        .then((res) => setUsers(res.data))
        .catch(() => {});
    }
  }, [clientId]);

  const closeModal = () => setIsModalOpen(false);

  const filteredSuppliers = suppliers?.filter((item: any) => {
    const term = searchTerm.toLowerCase();
    return (
      item.naziv?.toLowerCase().includes(term) ||
      item.kontaktOsoba?.toLowerCase().includes(term) ||
      item.email?.toLowerCase().includes(term) ||
      item.telefon?.toLowerCase().includes(term)
    );
  });

  const totalCount = filteredSuppliers?.length ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const pagedSuppliers = filteredSuppliers?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDelete = (supplier: any) => {
    axios
      .delete(`${BASE_API_URL}/${clientId}/suppliers/${supplier._id}`)
      .then(() =>
        setSuppliers((prev: any) => prev.filter((s: any) => s._id !== supplier._id))
      )
      .catch((err) => console.error(err));
  };

  const setField = (key: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
    if (key === "naziv") setNazivError(false);
  };

  const handleOpenAdd = () => {
    setEditingSupplier(null);
    setFormData(emptyForm);
    setNazivError(false);
    setUserSearch("");
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.naziv.trim()) {
      setNazivError(true);
      return;
    }
    const zaduzeni = formData.zaduzeniIds.map((id) => {
      const u = users.find((u: any) => u._id === id);
      return { _id: u._id, firstName: u.firstName, lastName: u.lastName };
    });
    const payload = { ...formData, zaduzeni };
    try {
      if (editingSupplier) {
        const res = await axios.put(
          `${BASE_API_URL}/${clientId}/suppliers/${editingSupplier._id}`,
          payload
        );
        setSuppliers((prev: any) =>
          prev.map((s: any) => (s._id === editingSupplier._id ? res.data : s))
        );
      } else {
        const res = await axios.post(`${BASE_API_URL}/${clientId}/suppliers`, {
          ...payload,
          clientId,
        });
        setSuppliers((prev: any) => [...(prev ?? []), res.data]);
      }
      closeModal();
    } catch (_) {}
  };

  const addZaduzeni = (userId: string) => {
    setFormData((prev) => ({ ...prev, zaduzeniIds: [...prev.zaduzeniIds, userId] }));
    setUserSearch("");
    setShowUserDrop(false);
  };

  const removeZaduzeni = (userId: string) => {
    setFormData((prev) => ({ ...prev, zaduzeniIds: prev.zaduzeniIds.filter((id) => id !== userId) }));
  };

  const filteredUserOptions = users.filter((u: any) => {
    if (formData.zaduzeniIds.includes(u._id)) return false;
    if (!userSearch) return true;
    return `${u.firstName} ${u.lastName}`.toLowerCase().includes(userSearch.toLowerCase());
  });

  const renderAvatar = (supplier: any, size = 36) => {
    const fontSize = Math.round(size * 0.33);
    return (
      <div
        className="sup-avatar"
        style={{
          width: size,
          height: size,
          fontSize,
          background: getAvatarGradient(supplier._id || supplier.naziv || ""),
        }}
      >
        {getInitials(supplier.naziv)}
      </div>
    );
  };

  return (
    <div className="mainContainer suppliers-page">
      {/* Header */}
      <div className="sup-head">
        <div>
          <h1 className="sup-title">
            Dobavljači
            <span className="sup-count">{suppliers?.length ?? 0}</span>
          </h1>
          <div className="sup-title-sub">Pregled svih dobavljača i komercijalnih kontakata</div>
        </div>
        <div className="sup-spacer" />
        {canCreate && (
          <button className="sup-cta" onClick={handleOpenAdd}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Dodaj dobavljača
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="sup-toolbar">
        <div className="sup-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.5-4.5" />
          </svg>
          <input
            placeholder="Pretraži po nazivu, kontaktu, emailu..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          />
          {searchTerm && (
            <button className="sup-search-clear" onClick={() => setSearchTerm("")}>×</button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="sup-table-wrap">
        <table className="sup-table">
          <thead>
            <tr>
              <th style={{ width: 52 }}></th>
              <th>Naziv</th>
              <th>Kontakt osoba</th>
              <th>Email</th>
              <th>Telefon</th>
              <th>Komercijalist</th>
              <th>Zaduženi</th>
              <th>Datum narudžbe</th>
              <th style={{ textAlign: "right" }}>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {pagedSuppliers?.length ? (
              pagedSuppliers.map((sup: any) => (
                <tr key={sup._id} onClick={() => canOpenProfile && navigate(`/suppliers/${sup._id}`)}>
                  <td>{renderAvatar(sup)}</td>
                  <td><span className="sup-name">{sup.naziv}</span></td>
                  <td><span className="sup-text">{sup.kontaktOsoba || "—"}</span></td>
                  <td>
                    {sup.email ? (
                      <div className="sup-mail">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="M3 7l9 6 9-6" />
                        </svg>
                        {sup.email}
                        <button className="copy-mail" title="Kopiraj" onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(sup.email); }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                            <rect x="9" y="9" width="11" height="11" rx="2" />
                            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                          </svg>
                        </button>
                      </div>
                    ) : <span className="sup-empty">—</span>}
                  </td>
                  <td><span className="sup-phone">{sup.telefon || "—"}</span></td>
                  <td><span className="sup-text">{sup.komercijalist || "—"}</span></td>
                  <td>
                    <div className="sup-zaduzeni-chips">
                      {normalizeZaduzeni(sup).length === 0 ? (
                        <span className="sup-empty">—</span>
                      ) : (
                        normalizeZaduzeni(sup).slice(0, 3).map((z: any) => (
                          <span key={z._id?.toString()} className="sup-person-chip">
                            <span
                              className="sup-person-dot"
                              style={{ background: personGradient(z._id?.toString() ?? "") }}
                            >
                              {personInitials(z)}
                            </span>
                            {z.firstName} {z.lastName}
                          </span>
                        ))
                      )}
                      {normalizeZaduzeni(sup).length > 3 && (
                        <span className="sup-person-chip sup-person-more">
                          +{normalizeZaduzeni(sup).length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td><span className="sup-date">{sup.datumNarudzbe || "—"}</span></td>
                  <td>
                    <div className="sup-actions">
                      {canOpenProfile && (
                        <button className="sup-action-btn primary" onClick={(e) => { e.stopPropagation(); navigate(`/suppliers/${sup._id}`); }}>
                          Otvori profil
                          <span className="sup-row-arrow">›</span>
                        </button>
                      )}
                      {canDelete && (
                        <Popconfirm
                          title="Da li ste sigurni da želite obrisati ovog dobavljača?"
                          onConfirm={() => handleDelete(sup)}
                          okText="Da"
                          cancelText="Ne"
                        >
                          <button className="sup-action-btn danger" onClick={(e) => e.stopPropagation()}>
                            Obriši
                          </button>
                        </Popconfirm>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8" }}>
                  {suppliers === undefined ? "Učitavanje..." : "Nema dobavljača"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="sup-foot">
          <div>
            Prikazano{" "}
            <strong>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)}</strong>{" "}
            od <strong>{totalCount}</strong>
          </div>
          <div className="sup-pager">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map((p) => (
              <button key={p} className={p === page ? "active" : ""} onClick={() => setPage(p)}>{p}</button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <AppModal
        open={isModalOpen}
        onClose={closeModal}
        icon={<TruckIcon />}
        eyebrow={editingSupplier ? "Uredi dobavljača" : "Novi dobavljač"}
        title={editingSupplier ? "Uredi podatke" : "Dodaj dobavljača"}
        description="Unesite osnovne podatke i ko će voditi saradnju iz vaše firme."
        onSubmit={handleSubmit}
        submitLabel={editingSupplier ? "Sačuvaj izmjene" : "Dodaj dobavljača"}
      >
        <div className="am-section">
          <div className="am-section-label">Podaci dobavljača</div>
          <div className="am-form-grid">

            <div className="am-field am-span-2">
              <label className="am-label">
                Naziv firme <span className="am-req">*</span>
              </label>
              <input
                className={`am-input${nazivError ? " am-input-error" : ""}`}
                placeholder="npr. Mercator d.o.o."
                value={formData.naziv}
                onChange={setField("naziv")}
              />
              {nazivError && <span className="am-error-msg">Naziv je obavezan</span>}
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">Kontakt osoba / broj</label>
              <input
                className="am-input"
                placeholder="Ime i prezime ili broj kontakta"
                value={formData.kontaktOsoba}
                onChange={setField("kontaktOsoba")}
              />
            </div>

            <div className="am-field">
              <label className="am-label">Email</label>
              <div className="am-input-affix">
                <span className="am-ic-lead">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3.5 6.5L12 13l8.5-6.5" />
                  </svg>
                </span>
                <input
                  className="am-input am-input-lead"
                  type="email"
                  placeholder="email@firma.com"
                  value={formData.email}
                  onChange={setField("email")}
                />
              </div>
            </div>

            <div className="am-field">
              <label className="am-label">Telefon</label>
              <div className="am-input-affix">
                <span className="am-ic-lead">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h3l1.5 5L8 9.5a12 12 0 0 0 6.5 6.5L16 14l5 1.5V19a2 2 0 0 1-2.2 2A16 16 0 0 1 3 5.2 2 2 0 0 1 5 3z" />
                  </svg>
                </span>
                <input
                  className="am-input am-input-lead"
                  type="tel"
                  placeholder="+381 XX XXX XXXX"
                  value={formData.telefon}
                  onChange={setField("telefon")}
                />
              </div>
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">
                Ime komercijaliste
                <span className="am-field-hint">sa strane dobavljača</span>
              </label>
              <input
                className="am-input"
                placeholder="Ime komercijaliste"
                value={formData.komercijalist}
                onChange={setField("komercijalist")}
              />
            </div>
          </div>
        </div>

        <div className="am-section">
          <div className="am-section-label">Interno upravljanje</div>
          <div className="am-form-grid">

            <div className="am-field am-span-2">
              <label className="am-label">Ko vodi ovog dobavljača</label>
              <div className="am-mu-picker">
                {formData.zaduzeniIds.length > 0 && (
                  <div className="am-mu-chips">
                    {formData.zaduzeniIds.map((id) => {
                      const u = users.find((u: any) => u._id === id);
                      if (!u) return null;
                      return (
                        <span key={id} className="am-mu-chip">
                          {u.firstName} {u.lastName}
                          <button type="button" onClick={() => removeZaduzeni(id)}>×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="am-mu-input-wrap">
                  <input
                    className="am-input"
                    placeholder="Pretraži i dodaj zaposlenog…"
                    value={userSearch}
                    onChange={(e) => { setUserSearch(e.target.value); setShowUserDrop(true); }}
                    onFocus={() => setShowUserDrop(true)}
                    onBlur={() => setTimeout(() => setShowUserDrop(false), 150)}
                  />
                  {showUserDrop && (
                    <div className="am-mu-dropdown">
                      {filteredUserOptions.length === 0 ? (
                        <div className="am-mu-empty">Nema rezultata</div>
                      ) : (
                        filteredUserOptions.map((u: any) => (
                          <button key={u._id} type="button" className="am-mu-option" onMouseDown={() => addZaduzeni(u._id)}>
                            <span className="am-mu-avatar" style={{ background: personGradient(u._id) }}>
                              {personInitials(u)}
                            </span>
                            {u.firstName} {u.lastName}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">Kada se poručuje</label>
              <input
                className="am-input"
                placeholder="npr. Svaki ponedeljak, 1. u mesecu…"
                value={formData.datumNarudzbe}
                onChange={setField("datumNarudzbe")}
              />
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">Napomena</label>
              <textarea
                className="am-textarea"
                placeholder="Dodatne napomene…"
                value={formData.napomena}
                onChange={setField("napomena")}
              />
            </div>
          </div>
        </div>
      </AppModal>
    </div>
  );
};

export default Suppliers;
